import { auditReports } from '@mi-front/api-clients';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, ChipProps, Grid } from '@mui/material';
import { ReactElement } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { aodRunCompletionReasonMessages } from '../../../messages';
import {
  AodRunCompletionReason,
  AodRunCompletionReasonKey,
  getAodRunCompletionReason,
} from '../../../utils/aod-run-completion-reason';
import { InfoM } from '../../aod-rule-view/components/InfoM';

interface AodRunCompletionReasonInfoProps {
  run: auditReports.AodRun;
}

const reasonLabelMessages: Record<AodRunCompletionReasonKey, MessageDescriptor> = {
  LOW_EFFICIENCY: aodRunCompletionReasonMessages.LOW_EFFICIENCY,
  MISSING_MATERIALS: aodRunCompletionReasonMessages.MISSING_MATERIALS,
};

/**
 * Maps completion reason to chip severity: missing materials is critical, low efficiency is a soft fail.
 */
function getReasonChipColor(reason: AodRunCompletionReasonKey): ChipProps['color'] {
  switch (reason) {
    case AodRunCompletionReason.MISSING_MATERIALS:
      return 'error';
    case AodRunCompletionReason.LOW_EFFICIENCY:
      return 'warning';
    default:
      return 'default';
  }
}

/** Icon matching the severity of each completion reason. */
function getReasonChipIcon(reason: AodRunCompletionReasonKey): ReactElement | undefined {
  switch (reason) {
    case AodRunCompletionReason.MISSING_MATERIALS:
      return <ErrorOutlineIcon />;
    case AodRunCompletionReason.LOW_EFFICIENCY:
      return <WarningAmberIcon />;
    default:
      return undefined;
  }
}

/**
 * Shows the structured completion reason chip for runs without export.
 * Renders as an inline grid item so it can sit beside status and efficiency.
 */
export function AodRunCompletionReasonInfo({ run }: AodRunCompletionReasonInfoProps) {
  const { formatMessage } = useIntl();
  const reason = getAodRunCompletionReason(run);
  const reasonMessage = reason ? reasonLabelMessages[reason] : undefined;

  if (!reason || !reasonMessage) {
    return null;
  }

  return (
    <Grid item>
      <InfoM label={formatMessage(aodRunCompletionReasonMessages.field)}>
        <Chip
          label={formatMessage(reasonMessage)}
          color={getReasonChipColor(reason)}
          icon={getReasonChipIcon(reason)}
          variant="outlined"
          size="small"
        />
      </InfoM>
    </Grid>
  );
}
